import { useEffect, useCallback, useRef } from 'react';
import { Editor, EditorEvents } from '@tiptap/react';
import { useDebouncedCallback } from '@mantine/hooks';
import { AiSuggestionAppliedMetaKey } from '@tiptap-pro/ai-toolkit';
import { runProofreaderCheck } from './styleResolver';

const STYLE_CHECK_DEBOUNCE_MS = 500;

/**
 * Headless component that drives the style-checking pipeline.
 * It listens for document edits and re-runs the per-node checker,
 * feeding the results into the AI Toolkit suggestion system via
 * `setSuggestions()`.
 *
 * Key behaviours:
 * - **Debounced re-checks** – avoids thrashing while the user is typing.
 * - **Skips suggestion-applied transactions** – when the AI Toolkit
 *   accepts or rejects a suggestion the document changes, but we skip
 *   the immediate re-check to prevent the loop where accepting a fix
 *   immediately triggers the reverse suggestion. The *next* genuine
 *   user edit will re-check as normal.
 */
export function StyleSuggestions({ editor }: { editor: Editor }) {
  const cancelledRef = useRef(false);

  const runStyleCheck = useDebouncedCallback(
    async () => {
      if (editor.isDestroyed) return;

      // Reset cancellation flag — any new update that arrives while our
      // async check is running will set this back to `true`, causing us
      // to discard stale results after the `await`.
      cancelledRef.current = false;

      try {
        await runProofreaderCheck(editor);

        if (cancelledRef.current || editor.isDestroyed) return;
      } catch (error) {
        console.error('[StyleSuggestions] Proofreader check failed:', error);
      }
    },
    { delay: STYLE_CHECK_DEBOUNCE_MS, flushOnUnmount: false },
  );

  const onEditorUpdate = useCallback(
    ({ transaction }: EditorEvents['update']) => {
      if (!transaction?.docChanged) return;

      // The AI Toolkit tags every accept/reject transaction with this meta
      // key. When present we skip the re-check to avoid firing a reverse
      // violation on the text that was just fixed.
      if (transaction.getMeta(AiSuggestionAppliedMetaKey)) return;

      // Cancel any in-flight async check so it discards stale results
      cancelledRef.current = true;
      runStyleCheck();
    },
    [runStyleCheck],
  );

  useEffect(() => {
    if (!editor || editor.isDestroyed) return;

    // Initial check
    runStyleCheck();

    editor.on('update', onEditorUpdate);

    return () => {
      cancelledRef.current = true;
      editor.off('update', onEditorUpdate);
    };
  }, [editor, onEditorUpdate, runStyleCheck]);

  return null;
}
